import type { SiteMetadata } from '../config/site-metadata'

/**
 * A JSON-serializable value permitted inside JSON-LD: primitives, nested
 * objects, or arrays. Recursive so nested schema.org entities (e.g. the
 * `FAQPage.mainEntity` Question list added in a later stage) type-check.
 */
export type JsonLdValue =
  | string
  | number
  | boolean
  | null
  | JsonLdValue[]
  | { [key: string]: JsonLdValue }

/**
 * A schema.org JSON-LD object: always carries `@context` and `@type`, and
 * permits arbitrary nested schema.org fields (typed as {@link JsonLdValue}).
 */
export type JsonLd = {
  '@context': 'https://schema.org'
  '@type': string
  [key: string]: JsonLdValue
}

/**
 * Build the `Organization` JSON-LD describing the brand behind the site.
 * Googlebot consumes this for knowledge-panel / sitelink enrichment.
 */
export const buildOrganizationJsonLd = (metadata: SiteMetadata): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: metadata.siteName,
  url: metadata.siteUrl,
  logo: metadata.ogImage,
})

/**
 * Build the `WebSite` JSON-LD describing the site itself.
 * Establishes the canonical site name and description for rich results.
 */
export const buildWebsiteJsonLd = (metadata: SiteMetadata): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: metadata.siteName,
  url: metadata.siteUrl,
  description: metadata.landingDescription,
})

/** A single FAQ entry — one question and its canonical answer. */
export interface FaqItem {
  question: string
  answer: string
}

/**
 * Build a `FAQPage` JSON-LD block from an array of question/answer pairs.
 * Each item becomes a `Question` entity with an `Answer` in `acceptedAnswer`.
 * Wire this into `<Seo additionalJsonLd={[buildFaqPageJsonLd(FAQ_ITEMS)]}>`
 * on any page that renders a FAQ section.
 */
export const buildFaqPageJsonLd = (items: FaqItem[]): JsonLd => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
})
